import { PolygonFilter } from './polygon-filter.model';

export function withoutViewport<T extends object>(criteria: T): T {
  const {
    northEastLat,
    northEastLng,
    southWestLat,
    southWestLng,
    isMapView,
    polygonFilter,
    ...rest
  } = criteria as T & {
    northEastLat?: number;
    northEastLng?: number;
    southWestLat?: number;
    southWestLng?: number;
    isMapView?: boolean;
    polygonFilter?: PolygonFilter;
  };
  return rest as T;
}

export function buildSearchRequest<T extends object>(
  state: {
    query: T | undefined;
    mapQuery?: T;
    mapPagination?: {pageIndex: number; pageSize: number};
    mapSort?: {active: string | null; direction: string | null};
    activePolygon: PolygonFilter | null;
    pagination: { pageIndex: number; pageSize: number };
    sort: { active: string | null; direction: string | null };
  },
  isMapView: boolean
): T & { polygonFilter?: PolygonFilter; isMapView: boolean } {
  const source = (isMapView ? state.mapQuery : state.query) ?? ({} as T);
  const pagination = isMapView ? state.mapPagination ?? state.pagination : state.pagination;
  const sort = isMapView ? state.mapSort ?? state.sort : state.sort;
  const criteria = state.activePolygon || !isMapView ? withoutViewport(source) : source;
  // Polygon belongs to committed domain state, never to an old query/form snapshot.
  const { polygonFilter: ignored, ...clean } = criteria as T & { polygonFilter?: PolygonFilter };
  return {
    ...clean,
    ...(isMapView && state.activePolygon
      ? {
          polygonFilter: state.activePolygon.map((p) => ({
            latitude: p.latitude,
            longitude: p.longitude,
          })),
        }
      : {}),
    isMapView,
    limit: pagination.pageSize,
    offset: isMapView ? 0 : pagination.pageIndex * pagination.pageSize,
    sort: sort.active,
    direction: sort.direction,
  } as unknown as T & { polygonFilter?: PolygonFilter; isMapView: boolean };
}

export function searchErrorKey(error: { error?: { detail?: string } }): string {
  const key = error?.error?.detail;
  return key?.startsWith('POLYGON_') ? key : 'POLYGON_SEARCH_FAILED';
}

/** Preserve only an explicitly searched rectangle; never derive it from viewport movement. */
export function mapSearchSnapshot<T extends object>(criteria: T, previous?: T): T {
  const keys = ['northEastLat', 'northEastLng', 'southWestLat', 'southWestLng'] as const;
  const current = criteria as Record<string, unknown>;
  const old = (previous ?? {}) as Record<string, unknown>;
  const bounds = keys.every(key => Number.isFinite(current[key])) ? current : old;
  return structuredClone({...withoutViewport(criteria),
    ...(keys.every(key => Number.isFinite(bounds[key])) ? Object.fromEntries(keys.map(key => [key, bounds[key]])) : {})}) as T;
}
